import { Navigate, useParams, useSearchParams } from 'react-router-dom';

import {
  resourceDetailPath,
  resourceListPath,
} from '../../utils/resource-routes';

/**
 * Forwards the pre-consolidation flat explorer URLs to the canonical
 * org-scoped ones (GDN-2723).
 *
 *   /admin-v2/resources/{type}[/{id}]?organization_id={org}
 *
 * Unlike the encounter/appointment shims this needs no lookup: the flat URL
 * already carried the organization in its query string, which is exactly what
 * the canonical path wants. Callers that still build this shape live outside
 * the migration (the data-import review browser's committed-resource links,
 * and an e2e spec), so they keep working until they migrate on their own
 * schedule.
 *
 * Without an `organization_id` there is no canonical address to forward to —
 * resources are always org-scoped — so that falls back to search rather than
 * inventing a scope.
 */
export default function FlatResourceRedirect() {
  const { resourceType, resourceId } = useParams<{
    resourceType?: string;
    resourceId?: string;
  }>();
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get('organization_id') ?? '';

  if (!resourceType || !organizationId) {
    return <Navigate to="/admin-v2" replace />;
  }

  const scope = { organizationId };
  return (
    <Navigate
      to={
        resourceId
          ? resourceDetailPath(scope, resourceType, resourceId)
          : resourceListPath(scope, resourceType)
      }
      replace
    />
  );
}
